import React, { useState, useEffect, useContext } from "react";
import { Modal, Button, Form, Spinner } from "react-bootstrap";
import Select from "react-select";
import {
  searchVehicle,
  searchEmployee,
  validatePaymentReference,
  validateVoucher,
  list_personal_wallets,
  get_personal_wallet_balance,
} from "../../api/otogas";
import CustomersContext from "../../context/otogas/customers/customersContext";
import debounce from "lodash.debounce";
import { set } from "date-fns";

// Config for payment types with rules for each
const paymentTypeConfig = {
  0: {
    requiresVehicle: true,
    requiresPaymentReference: true,
    label: "Mpesa",
    requiresEmployee: false,
    requiresPaymentValidation: true,
  },
  1: {
    requiresVehicle: true,
    requiresWalletCheck: true,
    label: "Wallet",
    requiresEmployee: false,
    requiresPaymentValidation: false,
  },
  2: {
    requiresVehicle: true,
    label: "new Conversions",
    requiresEmployee: false,
    requiresPaymentValidation: false,
  },
  3: {
    requiresEmployee: true,
    label: "Operational loss",
    requiresVehicle: false,
    requiresPaymentValidation: false,
  },
  4: {
    requiresEmployee: true,
    label: "Bank transfer",
    requiresVehicle: false,
    requiresPaymentValidation: false,
  },
  5: {
    requiresEmployee: true,
    requiresPaymentReference: true,
    label: "Salary",
    requiresVehicle: false,
    requiresPaymentValidation: true,
  },
  6: {
    requiresPaymentReference: true,
    label: "Insurance",
    requiresVehicle: false,
    requiresEmployee: true,
    requiresPaymentValidation: false,
  },
  7: {
    requiresPaymentReference: true,
    label: "Voucher",
    requiresVehicle: true,
    requiresEmployee: false,
    requiresPaymentValidation: true,
  },
  8: {
    requiresEmployee: true,
    label: "Calibration",
    requiresPaymentValidation: false,
  },
  9: {
    requiresEmployee: true,
    label: "Compesational fuel",
    requiresPaymentValidation: false,
  },
  10: {
    requiresEmployee: true,
    label: "Batch Voucher",
    requiresVehicle: false,
    requiresPaymentValidation: false,
  },
  11: {
    requiresEmployee: false,
    label: "Personal Wallet",
    requiresVehicle: true,
    requiresPaymentValidation: false,
    requiresWalletOwnerCheck: true,
  },
};

// ===== BAND HELPERS =====

// Find the band whose range contains the given quantity
const getBandForQuantity = (bands, quantity) => {
  if (!Array.isArray(bands) || !bands.length) return null;
  const qty = parseFloat(quantity);
  if (isNaN(qty) || qty <= 0) return null;

  return (
    bands.find((band) => {
      const min = band.minLitres ?? 0;
      const max = band.maxLitres;

      if (max === null || max === undefined) {
        // Open-ended band: minLitres and above
        return qty >= min;
      }

      return qty >= min && qty <= max;
    }) || null
  );
};

// Decide which price to use based on base price + bands for a given quantity
const getPriceAndBandForQuantity = (basePrice, bands, quantity) => {
  const matchedBand = getBandForQuantity(bands, quantity);
  if (matchedBand && matchedBand.bandPrice != null) {
    return { priceToApply: matchedBand.bandPrice, matchedBand };
  }
  return { priceToApply: basePrice, matchedBand: null };
};

const getBandPriceForVoucherAmount = (amount, basePrice, bands) => {
  const amt = parseFloat(amount);
  if (isNaN(amt) || amt <= 0) {
    return { priceToUse: basePrice, matchedBand: null };
  }

  if (!Array.isArray(bands) || bands.length === 0) {
    return { priceToUse: basePrice, matchedBand: null };
  }

  const EPS = 0.01; // tolerance for rounding

  let bestMatch = null;

  for (const band of bands) {
    const min = band.minLitres ?? 0;
    const max = band.maxLitres ?? Infinity;
    const bandPrice = band.bandPrice;

    if (!bandPrice || bandPrice <= 0) continue;

    // quantity if we used THIS band's price
    const qty = amt / bandPrice;

    // allow a small epsilon on boundaries
    if (qty + EPS < min || qty - EPS > max) {
      continue;
    }

    // pick the lowest priced band, or whatever rule you want
    if (!bestMatch || bandPrice < bestMatch.band.bandPrice) {
      bestMatch = { band, qty };
    }
  }

  if (bestMatch) {
    return {
      priceToUse: bestMatch.band.bandPrice,
      matchedBand: bestMatch.band,
    };
  }

  // fallback when no band fits this amount
  return { priceToUse: basePrice, matchedBand: null };
};


const VarianceClearanceModal = ({ show, handleClose, selectedVariance }) => {
  const [paymentType, setPaymentType] = useState(null);
  const [quantity, setQuantity] = useState(0);
  const [amountPayable, setAmountPayable] = useState(0);
  const [paymentReference, setPaymentReference] = useState("");
  const [comment, setComment] = useState("");
  const [voucherCode, setVoucherCode] = useState("");
  const [vehicleDetails, setVehicleDetails] = useState(null);

  // price = applied price (with band if any)
  const [price, setPrice] = useState(null);

  // base price (API price before band adjustment)
  const [basePrice, setBasePrice] = useState(null);

  // bands from API + the one currently applied
  const [activeBandPrices, setActiveBandPrices] = useState([]);
  const [appliedBand, setAppliedBand] = useState(null);

  const [walletBalance, setWalletBalance] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [paymentReferenceValid, setPaymentReferenceValid] = useState(null);
  const [loading, setLoading] = useState(false);

  // Error states
  const [vehicleError, setVehicleError] = useState("");
  const [quantityError, setQuantityError] = useState("");
  const [paymentReferenceError, setPaymentReferenceError] = useState("");
  const [walletOptions, setWalletOptions] = useState([]);
  const [selectedWallet, setSelectedWallet] = useState(null);

  const {
    vehicles,
    fetchVehicles,
    payment_types: paymentTypes,
    listPaymentTypes,
    addSale,
  } = useContext(CustomersContext);

  const [vehicleOptions, setVehicleOptions] = useState([]);

  useEffect(() => {
    listPaymentTypes();
    resetForm();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // Automatically fetch the price based on payment type
    if (paymentType || paymentType === 0) {
      const config = paymentTypeConfig[paymentType];
      if (config?.requiresEmployee && selectedVariance?.userCode) {
        fetchPriceForUser(selectedVariance.userCode);
      } else if (config?.requiresVehicle && vehicleDetails?.vehicleCode) {
        fetchPriceForVehicle(vehicleDetails.vehicleCode);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paymentType, selectedVariance?.userCode, vehicleDetails?.vehicleCode]);

  const fetchPriceForUser = async (userCode) => {
    setIsLoading(true);
    try {
      const priceData = await searchEmployee(userCode);
      if (priceData) {
        setPrice(priceData.price);
        setBasePrice(priceData.price);
      } else {
        alert("Price not found or error occurred.");
      }
    } catch (error) {
      console.error("Error fetching price:", error);
    }
    setIsLoading(false);
  };

  console.log("selected wallet", selectedWallet);

  const searchWalletUser = async (phoneNumber) => {
    setIsLoading(true);
    try {
      const res = await list_personal_wallets(
        "", // walletId
        phoneNumber,
        "", // name
        "", // identificationNumber
        1, // page
        10 // pageSize
      );

      if (res?.responseCode === 1) {
        const options = res.responseObject.data.map((wallet) => ({
          label: `${wallet.firstName} ${wallet.lastName} (${wallet.phoneNumber})`,
          value: wallet.walletId,
          credit: wallet.credit,
          userCode: wallet.userCode,
        }));

        setWalletOptions(options);
      } else {
        setWalletOptions([]);
      }
    } catch (error) {
      console.error("Error searching wallet user:", error);
      setWalletOptions([]);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchPriceForVehicle = async (vehicleCode) => {
    setIsLoading(true);
    try {
      const vehicleData = await searchVehicle(
        selectedVariance?.stationCode,
        vehicleDetails.vehicleRegistrationNumber,
        selectedVariance.shiftNumber
      );

      if (vehicleData?.responseObject) {
        const priceFromApi = vehicleData.responseObject.price;
        const vehicleWalletBal = vehicleData.responseObject.walletBalance ?? null;
        const bandsFromApi = vehicleData.responseObject.activeBandPrices || [];

        if (priceFromApi != null) {
          setBasePrice(priceFromApi);
          setPrice(priceFromApi);
          setActiveBandPrices(bandsFromApi);
          setAppliedBand(null);

          // Only override wallet balance for VEHICLE WALLET (type 1)
          if (paymentType === 1) {
            setWalletBalance(vehicleWalletBal);
          }
        } else {
          setVehicleError("Price not found or error occurred.");
        }
      } else {
        setVehicleError("Error fetching vehicle details.");
      }
    } catch (error) {
      setVehicleError("Error fetching price.");
      console.error("Error fetching price:", error);
    } finally {
      setIsLoading(false);
    }
  };

  // Recalculate applied price + total when quantity changes (for vehicle-based, non-voucher flows)
  useEffect(() => {
    const config = paymentTypeConfig[paymentType] || {};
    const numericQty = parseFloat(quantity);

    // Skip all band re-calcs for voucher: handled in voucher validation
    if (paymentType === 7) return;

    if (
      !config.requiresVehicle ||
      basePrice == null ||
      isNaN(numericQty) ||
      numericQty <= 0
    ) {
      if (config.requiresVehicle) {
        setAmountPayable(0);
        setQuantityError("");
        setAppliedBand(null);
        if (basePrice != null) {
          setPrice(basePrice);
        } else {
          setPrice(null);
        }
      }
      return;
    }

    const { priceToApply, matchedBand } = getPriceAndBandForQuantity(
      basePrice,
      activeBandPrices,
      numericQty
    );

    setPrice(priceToApply);
    setAppliedBand(matchedBand);

    const total = priceToApply * numericQty;
    setAmountPayable(total.toFixed(2));

    if ((paymentType === 1 || paymentType === 11) && walletBalance !== null) {
      if (total > walletBalance) {
        setQuantityError("Entered quantity exceeds wallet balance");
        setQuantity(0);
        setAmountPayable(0);
      } else {
        setQuantityError("");
      }
    } else {
      setQuantityError("");
    }
  }, [
    quantity,
    basePrice,
    walletBalance,
    paymentType,
    activeBandPrices,
  ]);

  const validatePayment = async () => {
    setPaymentReferenceError("");
    setPaymentReferenceValid(null);
    setIsLoading(true);

    console.log("Payment type:", paymentType);

    if (paymentType === 7) {
      // Voucher validation with band logic
      try {
        const response = await validateVoucher(paymentReference);
        if (response && response.responseCode === 1) {
          const { amount } = response.responseObject;

          const effectiveBasePrice = basePrice ?? price;

          const { priceToUse, matchedBand } = getBandPriceForVoucherAmount(
            amount,
            effectiveBasePrice,
            activeBandPrices
          );

          // Apply band price & band info
          setPrice(priceToUse);
          setAppliedBand(matchedBand);

          // Quantity = amount / applied (band) price
          const qty = amount / priceToUse;
          setQuantity(qty.toFixed(2));

          // Total payable is voucher amount
          setAmountPayable(amount);

          setPaymentReferenceValid(response.responseMessage);
        } else {
          setPaymentReferenceError(response.responseMessage);
        }
      } catch (error) {
        console.error("Error validating voucher:", error);
        setPaymentReferenceError("Error validating voucher.");
      } finally {
        setIsLoading(false);
      }
    } else {
      // Mpesa / Salary / etc
      try {
        const response = await validatePaymentReference(
          selectedVariance.dispenserCode,
          paymentReference
        );

        if (response && response.responseCode === 1) {
          const parsedAmountPayable = parseFloat(amountPayable);
          const balanceUsage = parseFloat(response.responseObject.usageBalance);
          console.log("Parsed amount payable:", parsedAmountPayable);
          console.log("Balance usage:", balanceUsage);
          console.log(parsedAmountPayable <= balanceUsage);

          if (parsedAmountPayable <= balanceUsage) {
            setPaymentReferenceValid(response.responseMessage);
          } else {
            setPaymentReferenceError(response.responseMessage);
          }
        } else {
          setPaymentReferenceError(response.responseMessage);
        }
      } catch (error) {
        console.log("Error occurred during validation:", error);
        setPaymentReferenceValid("Error validating payment.");
      } finally {
        setIsLoading(false);
      }
    }
  };

  const handleSubmit = async (e) => {
    setLoading(true);
    e.preventDefault();
    let hasErrors = false;

    const config = paymentTypeConfig[paymentType];

    // If employee is required, use userCode as vehicleCode
    const vehicleCode = config?.requiresEmployee
      ? selectedVariance?.userCode
      : vehicleDetails?.vehicleCode;

    if (!vehicleCode && config?.requiresVehicle) {
      setVehicleError("Vehicle registration is required.");
      hasErrors = true;
    } else {
      setVehicleError("");
    }

    if (quantity <= 0 && paymentType !== 3) {
      setQuantityError("Quantity must be greater than 0.");
      hasErrors = true;
    } else {
      setQuantityError("");
    }

    if (config?.requiresPaymentReference && paymentReference.trim() === "") {
      setPaymentReferenceError("Payment reference is required.");
      hasErrors = true;
    } else if (config?.requiresPaymentValidation && !paymentReferenceValid) {
      setPaymentReferenceError("Please validate the payment reference.");
      hasErrors = true;
    } else {
      setPaymentReferenceError("");
    }

    if (hasErrors) return;

    const payload = {
      walletId: selectedWallet?.value || "",
      vehicleCode,
      paymentTypeCode: paymentType,
      nozzleCode: selectedVariance?.nozzleCode,
      shiftNumber: selectedVariance?.shiftNumber,
      quantity: parseFloat(quantity),
      price: parseFloat(price ?? 0), // applied price (band or base)
      dispenserCode: selectedVariance?.dispenserCode,
      comment,
      paymentDetails: [
        {
          transactionReference: paymentReference,
          transactionAmount: amountPayable,
        },
      ],
    };

    try {
      const response = await addSale(payload);
      if (response && response.error) {
        setPaymentReferenceError(
          "Error submitting form: " + response.error.message
        );
      } else {
        resetForm();
        handleClose();
      }
    } catch (error) {
      setPaymentReferenceError(
        "An unexpected error occurred. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setVehicleDetails(null);
    setQuantity(0);
    setAmountPayable(0);
    setPaymentReference("");
    setComment("");
    setPrice(0);
    setBasePrice(null);
    setActiveBandPrices([]);
    setAppliedBand(null);
    setWalletBalance(null);
    setPaymentReferenceValid(null);
    setIsLoading(false);
    setPaymentReferenceError("");
    setVehicleError("");
    setQuantityError("");
    setVehicleOptions([]);
    setSelectedWallet(null);
  };

  const handleChange = (selectedOption) => {
    setVehicleDetails({
      vehicleCode: selectedOption.value,
      vehicleRegistrationNumber: selectedOption.label,
    });
  };

  const debouncedFetchVehicles = debounce((regNo) => {
    fetchVehicles(1, 10, "", regNo, "", "", true);
  }, 500);

  useEffect(() => {
    if (vehicles) {
      const options = vehicles.map((vehicle) => ({
        value: vehicle.vehicleCode,
        label: vehicle.vehicleRegistrationNumber,
      }));
      setVehicleOptions(options);
    }
  }, [vehicles]);

  console.log(isLoading, "isLoading");
  console.log(paymentType, "paymentType");
  console.log(quantity, "quantity");
  console.log(vehicleError, "vehicleError");
  console.log(quantityError, "quantityError");
  console.log(comment, "comment");
  console.log(paymentReference, "paymentReference");
  console.log(walletBalance, "walletBalance");
  console.log(basePrice, "basePrice");
  console.log(activeBandPrices, "activeBandPrices");
  console.log(appliedBand, "appliedBand");

  const hasBands =
    Array.isArray(activeBandPrices) && activeBandPrices.length > 0;

  return (
    <Modal
      show={show}
      onHide={() => {
        resetForm();
        handleClose();
      }}
      backdrop="static"
      keyboard={false}
    >
      <Modal.Header closeButton>
        <Modal.Title>Variance Clearance</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form onSubmit={handleSubmit}>
          <Form.Group controlId="paymentType">
            <Form.Label>Select Payment Type</Form.Label>
            <Select
              options={paymentTypes.map((type) => ({
                value: type.paymentTypeId,
                label: type.paymentTypeName.replace(/_/g, " "),
              }))}
              onChange={(selectedOption) => {
                resetForm();
                setPaymentType(selectedOption.value);
              }}
              placeholder="Select Payment Type"
            />
          </Form.Group>

          {paymentTypeConfig[paymentType]?.requiresWalletOwnerCheck && (
            <Form.Group controlId="walletSearch">
              <Form.Label>Search Personal Wallet</Form.Label>
              <Select
                options={walletOptions}
                onInputChange={(inputValue) => {
                  if (inputValue.length >= 3) {
                    searchWalletUser(inputValue);
                  }
                }}
                onChange={async (selectedOption) => {
                  setSelectedWallet(selectedOption);
                  setIsLoading(true);
                  try {
                    const res = await get_personal_wallet_balance(
                      selectedOption.value
                    );
                    if (res?.responseCode) {
                      setWalletBalance(res.responseObject);
                    } else {
                      setWalletBalance(0);
                    }
                  } catch (error) {
                    console.error("Error fetching wallet balance:", error);
                    setWalletBalance(0);
                  } finally {
                    setIsLoading(false);
                  }
                }}
                placeholder="Type phone number..."
                isClearable
                isLoading={isLoading}
              />
            </Form.Group>
          )}

          {/* Vehicle select */}
          {paymentTypeConfig[paymentType]?.requiresVehicle && (
            <Form.Group controlId="vehicleRegistration">
              <Form.Label>Vehicle Registration</Form.Label>
              <Select
                options={vehicleOptions}
                onChange={handleChange}
                onInputChange={(inputValue) =>
                  debouncedFetchVehicles(inputValue)
                }
                placeholder="Select Vehicle"
                value={vehicleOptions.find(
                  (option) => option.value === vehicleDetails?.vehicleCode
                )}
                isClearable
              />
              {vehicleError && (
                <Form.Text className="text-danger">{vehicleError}</Form.Text>
              )}
              {isLoading && <Spinner animation="border" size="sm" />}
            </Form.Group>
          )}

          {/* Wallet Details Section - Shown when Wallet is the selected Payment Type */}
          {paymentTypeConfig[paymentType]?.requiresWalletCheck &&
            walletBalance !== null && (
              <Form.Group controlId="walletDetails">
                <Form.Label>Vehicle Wallet Details</Form.Label>
                <p>
                  <strong>Base price per unit:</strong>{" "}
                  {basePrice != null ? basePrice : price}
                </p>
                {appliedBand && (
                  <p>
                    <strong>Band price applied:</strong> {price}{" "}
                    <small>
                      ({appliedBand.minLitres} -{" "}
                      {appliedBand.maxLitres || "above"} L)
                    </small>
                  </p>
                )}
                <p>
                  <strong>Wallet Balance:</strong> {walletBalance}
                </p>
              </Form.Group>
            )}

          {/* Active Band Prices display */}
          {paymentTypeConfig[paymentType]?.requiresVehicle && hasBands && (
            <Form.Group controlId="bandPrices">
              <Form.Label>Active Band Prices</Form.Label>
              <ul className="mb-0">
                {activeBandPrices
                  .slice()
                  .sort((a, b) => a.minLitres - b.minLitres)
                  .map((band, idx) => {
                    const isApplied =
                      appliedBand &&
                      band.minLitres === appliedBand.minLitres &&
                      (band.maxLitres ?? null) ===
                        (appliedBand.maxLitres ?? null) &&
                      band.bandPrice === appliedBand.bandPrice;

                    return (
                      <li key={idx}>
                        {band.minLitres} - {band.maxLitres || "above"} L @{" "}
                        {band.bandPrice}
                        {isApplied && <strong> (applied)</strong>}
                      </li>
                    );
                  })}
              </ul>
            </Form.Group>
          )}

          {/* Employee Details */}
          {paymentTypeConfig[paymentType]?.requiresEmployee &&
            selectedVariance && (
              <div>
                <p>
                  <strong>Name:</strong> {selectedVariance?.name || "N/A"}
                </p>
                <p>
                  <strong>User Code:</strong>{" "}
                  {selectedVariance?.userCode || "N/A"}
                </p>
                {isLoading && <Spinner animation="border" size="sm" />}
              </div>
            )}

          {/* Quantity */}
          {price && paymentType === 7 ? (
            // Voucher: quantity is determined by voucher amount (we already set it)
            <Form.Group controlId="quantity">
              <Form.Label>Quantity</Form.Label>
              <Form.Control type="text" value={quantity} disabled />
              <Form.Text className="text-muted">
                Quantity is determined by the voucher and band price.
              </Form.Text>
            </Form.Group>
          ) : (
            price && (
              <Form.Group controlId="quantity">
                <Form.Label>Quantity</Form.Label>
                <Form.Control
                  type="text" // Allow '-' and decimals
                  value={quantity}
                  onChange={(e) => {
                    const value = e.target.value.trim();
                    const parsedValue = parseFloat(value);

                    if (paymentType === 3) {
                      // Operational loss: allow negative / "-"
                      if (
                        value === "-" ||
                        value === "" ||
                        !isNaN(parsedValue)
                      ) {
                        setQuantityError("");
                        setQuantity(value);
                        setAmountPayable(
                          !isNaN(parsedValue)
                            ? (parsedValue * price).toFixed(2)
                            : "0"
                        );
                      } else {
                        setQuantityError("Invalid input");
                      }
                    } else if (!isNaN(parsedValue) && parsedValue >= 0) {
                      // Other payment types: positive decimals
                      setQuantityError("");
                      setQuantity(value);
                      setAmountPayable((parsedValue * price).toFixed(2));
                    } else {
                      setQuantityError("Quantity must be greater than 0");
                      setQuantity("");
                      setAmountPayable("0");
                    }
                  }}
                  required
                />
                {quantityError && (
                  <Form.Text className="text-danger">
                    {quantityError}
                  </Form.Text>
                )}
              </Form.Group>
            )
          )}

          {amountPayable > 0 && (
            <Form.Group controlId="totalAmount">
              <Form.Label>Total Payable Amount</Form.Label>
              <p>
                <strong>{amountPayable}</strong>
              </p>
            </Form.Group>
          )}

          {/* Payment Reference */}
          {paymentTypeConfig[paymentType]?.requiresPaymentReference && (
            <Form.Group controlId="paymentReference">
              <Form.Label>Payment Reference</Form.Label>
              <Form.Control
                type="text"
                value={paymentReference}
                onChange={(e) => {
                  setPaymentReference(e.target.value);
                  if (paymentReferenceValid !== null) {
                    setPaymentReferenceValid(null);
                  }
                }}
                required
              />

              {paymentReferenceError && (
                <div className="mt-2">
                  <Form.Text className="text-danger">
                    {paymentReferenceError}
                  </Form.Text>
                </div>
              )}

              {paymentReferenceValid && (
                <div className="mt-2">
                  <Form.Text className="text-success">
                    {paymentReferenceValid}
                  </Form.Text>
                </div>
              )}

              {paymentType !== null &&
                paymentType !== undefined &&
                paymentTypeConfig[paymentType]?.requiresPaymentValidation && (
                  <div className="mt-3">
                    <Button
                      onClick={validatePayment}
                      disabled={isLoading || paymentReferenceValid !== null}
                    >
                      {isLoading ? (
                        <Spinner animation="border" size="sm" />
                      ) : (
                        "Validate Payment"
                      )}
                    </Button>
                  </div>
                )}
            </Form.Group>
          )}

          {/* Comment */}
          <Form.Group controlId="comment">
            <Form.Label>Comment</Form.Label>
            <Form.Control
              as="textarea"
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              required
            />
          </Form.Group>

          {/* Submit Button */}
          <Button
            variant="primary"
            type="submit"
            disabled={!comment || isLoading || !quantity}
          >
            {loading ? (
              <>
                <Spinner
                  as="span"
                  animation="border"
                  size="sm"
                  role="status"
                  aria-hidden="true"
                />{" "}
                Submitting...
              </>
            ) : (
              "Submit"
            )}
          </Button>
        </Form>
      </Modal.Body>
    </Modal>
  );
};

export default VarianceClearanceModal;
